import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { CallCenterPhoneNumbersApi } from '@core/api';
import * as Entities from '@shared/entities';
import { CallCenterPhoneNumberImportRequest } from '@shared/entities/entities';
import { ListResponse, Result } from '@shared/models';
import { extractErrorMessage } from '@shared/utils';
import { commandResult, emptyListResponse, failureResult } from './store-helpers';

export const CallCenterPhoneNumbersStore = signalStore(
  { providedIn: 'root' },
  withState({
    phoneNumbers: emptyListResponse<Entities.CallCenterPhoneNumber>(),
    phoneNumberTypes: emptyListResponse<Entities.CallCenterPhoneNumberType>(),
    loadingPhoneNumbers: false,
    loadedPhoneNumbers: false,
    loadingTypes: false,
    loadedTypes: false,
    commandInProgress: false,
    lastCommandName: null as string | null,
    lastCommandResult: null as Result<unknown> | null,
    errorMessage: null as string | null,
  }),
  withComputed(({ phoneNumbers, phoneNumberTypes, loadingPhoneNumbers, loadingTypes, commandInProgress }) => ({
    records: computed(() => phoneNumbers().records),
    typeRecords: computed(() => phoneNumberTypes().records),
    isBusy: computed(() => loadingPhoneNumbers() || loadingTypes() || commandInProgress()),
    byId: computed(
      () => new Map(phoneNumbers().records.map((phoneNumber) => [phoneNumber.callCenterPhoneNumberId, phoneNumber]))
    ),
    unallocatedPhoneNumbers: computed(
      () =>
        ({
          records: phoneNumbers().records.filter((phoneNumber) => {
            return phoneNumber.allocated === null && phoneNumber.callCenterPhoneNumberTypeId === 1;
          }),
        }) as ListResponse<Entities.CallCenterPhoneNumber>
    ),
  })),
  withMethods((store, api = inject(CallCenterPhoneNumbersApi)) => {
    let phoneNumbersRequestId = 0;
    let phoneNumberTypesRequestId = 0;

    const loadCallCenterPhoneNumbers = (force = false): void => {
      if (!force && store.loadedPhoneNumbers()) {
        return;
      }

      const requestId = ++phoneNumbersRequestId;
      patchState(store, { loadingPhoneNumbers: true, errorMessage: null });

      void api
        .loadCallCenterPhoneNumbers(force)
        .then((phoneNumbers) => {
          if (requestId === phoneNumbersRequestId) {
            patchState(store, { phoneNumbers, loadedPhoneNumbers: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === phoneNumbersRequestId) {
            patchState(store, {
              errorMessage: extractErrorMessage(error, 'Unable to load call center phone numbers.'),
            });
          }
        })
        .finally(() => {
          if (requestId === phoneNumbersRequestId) {
            patchState(store, { loadingPhoneNumbers: false });
          }
        });
    };

    const loadCallCenterPhoneNumberTypes = (force = false): void => {
      if (!force && store.loadedTypes()) {
        return;
      }

      const requestId = ++phoneNumberTypesRequestId;
      patchState(store, { loadingTypes: true, errorMessage: null });

      void api
        .loadCallCenterPhoneNumberTypes(force)
        .then((phoneNumberTypes) => {
          if (requestId === phoneNumberTypesRequestId) {
            patchState(store, { phoneNumberTypes, loadedTypes: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === phoneNumberTypesRequestId) {
            patchState(store, {
              errorMessage: extractErrorMessage(error, 'Unable to load call center phone number types.'),
            });
          }
        })
        .finally(() => {
          if (requestId === phoneNumberTypesRequestId) {
            patchState(store, { loadingTypes: false });
          }
        });
    };

    return {
      loadCallCenterPhoneNumbers,
      loadCallCenterPhoneNumberTypes,
      import(request: CallCenterPhoneNumberImportRequest[]): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'import',
          lastCommandResult: null,
          errorMessage: null,
        });

        void api
          .import(request)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('import', result) });
            if (result.successful) {
              loadCallCenterPhoneNumbers(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to import call center phone number.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to import call center phone number.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('import', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      create(callCenterPhoneNumber: Entities.CallCenterPhoneNumber): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'create',
          lastCommandResult: null,
          errorMessage: null,
        });

        void api
          .create(callCenterPhoneNumber)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('create', result) });
            if (result.successful) {
              loadCallCenterPhoneNumbers(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to create call center phone number.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create call center phone number.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('create', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      update(id: number, callCenterPhoneNumber: Entities.CallCenterPhoneNumber): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'update',
          lastCommandResult: null,
          errorMessage: null,
        });

        void api
          .update(id, callCenterPhoneNumber)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('update', result) });
            if (result.successful) {
              loadCallCenterPhoneNumbers(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to update call center phone number.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update call center phone number.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('update', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      delete(id: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'delete',
          lastCommandResult: null,
          errorMessage: null,
        });

        void api
          .delete(id)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('delete', result) });
            if (result.successful) {
              loadCallCenterPhoneNumbers(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to delete call center phone number.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to delete call center phone number.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('delete', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      getById(callCenterPhoneNumberId: number): Entities.CallCenterPhoneNumber | undefined {
        return store
          .phoneNumbers()
          .records.find((phoneNumber) => phoneNumber.callCenterPhoneNumberId === callCenterPhoneNumberId);
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null });
      },
    };
  })
);
